'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import {
  CampusIcon,
  CampusSvgProvider,
  EventCard,
  EventCardSkeleton,
  GroupCard,
  SeeJesusSheet,
  ServeCard,
  ToggleChip,
  campusSvgMap,
  pluralEvents,
  pluralGroups,
  pluralOpportunities,
  type LocationGroup,
} from '@church/nextjs-ui/website';
import type { SiteSearchData } from '@/lib/types';
import { fetchLocations, fetchSearch } from '@/lib/api';
import { getSelectedCampus, onCampusChange, type SelectedCampus } from '@/lib/campus';

export interface SiteSearchCounts {
  /** On show, after the campus scope */
  events: number;
  groups: number;
  opportunities: number;
  /** Everything MinistryPlatform matched, at any campus */
  all: number;
}

export interface SiteSearchProps {
  apiBaseUrl: string;
  /** The visitor's words, from the page's `?s=` */
  query: string;
  /** The search box submits here (default: this page's path) with `queryParam` (default `s`) */
  searchPath?: string;
  queryParam?: string;
  /** Render the search box and campus chips here (the theme's title band) instead of above the results */
  toolbarContainer?: HTMLElement | null;
  /** The theme printed no count line (WordPress found nothing): the widget writes one */
  showCount?: boolean;
  /** Override the details link bases (default: what the API says) */
  eventDetailsUrl?: string;
  groupDetailsUrl?: string;
  serveDetailsUrl?: string;
  /** How many cards a section shows before "Show more" */
  pageSize?: number;
  /** Fires with the counts on show (or null on failure): the host updates the page's count line */
  onResult?: (counts: SiteSearchCounts | null) => void;
}

type Status = 'loading' | 'ready' | 'error';
type Scope = 'campus' | 'all';

/** Congregation_ID 1: church-wide, shown alongside any campus */
const CHURCH_WIDE = 1;

const EYEBROW = 'text-xs font-bold tracking-widest text-[#299c8f] uppercase';
const GRID = 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3';

function Section({
  title,
  count,
  countLabel,
  seeAllHref,
  seeAllLabel,
  shown,
  total,
  onMore,
  children,
}: {
  title: string;
  count: number;
  countLabel: string;
  seeAllHref: string;
  seeAllLabel: string;
  shown: number;
  total: number;
  onMore: () => void;
  children: ReactNode;
}) {
  if (count === 0) return null;
  const more = total - shown;
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div className="flex items-baseline gap-3">
          <h2 className="text-2xl leading-none font-extrabold tracking-tight text-[#1c2b39] uppercase">
            {title}
          </h2>
          <span className="text-sm font-semibold text-gray-500">{countLabel}</span>
        </div>
        <a
          href={seeAllHref}
          className="inline-flex items-center gap-1 text-sm font-bold text-[#299c8f] no-underline transition-all hover:translate-x-0.5"
        >
          {seeAllLabel}
          <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        </a>
      </div>
      <div className={GRID}>{children}</div>
      {more > 0 && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onMore}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#1c2b39] ring-1 ring-gray-200 transition-all ring-inset hover:scale-105 hover:shadow-md active:scale-95"
          >
            Show {more} more
            <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Search again without going back to the header: a plain GET form, so the
 * whole page (WordPress's results included) searches the new words.
 */
function SearchBox({
  query,
  searchPath,
  queryParam,
}: {
  query: string;
  searchPath: string;
  queryParam: string;
}) {
  return (
    <form method="get" action={searchPath} className="flex w-full min-w-0 gap-2" role="search">
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          name={queryParam}
          defaultValue={query}
          placeholder="Search again"
          aria-label="Search the site"
          autoComplete="off"
          className="w-full rounded-full border border-gray-200 bg-white py-3 pr-4 pl-11 text-sm text-[#1c2b39] shadow-sm placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none"
        />
      </div>
      <button
        type="submit"
        className="inline-flex shrink-0 items-center rounded-full bg-[#299c8f] px-5 py-3 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
      >
        Search
      </button>
    </form>
  );
}

/** "Lake Orion" from the cookie, else from the campus list, else "your campus". */
function campusName(campus: SelectedCampus, locations: LocationGroup[]): string {
  if (campus.name) return campus.name.replace(/\s+campus$/i, '');
  for (const group of locations) {
    const found = group.congregations.find((c) => c.id === campus.congregationId);
    if (found) return found.name.replace(/\s+campus$/i, '');
  }
  return 'your campus';
}

/**
 * The MinistryPlatform side of the website's search results: the events,
 * Life Groups and serve opportunities matching the visitor's words, each as
 * the same card its finder shows. Cards link to the details pages; the
 * "See all" links open the finder prefiltered to the same words.
 *
 * With a campus picked in the site header, the sections show that campus
 * and church-wide only, with a chip to widen to every campus. WordPress's
 * own results below are not campus-specific and are left alone.
 */
export function SiteSearch({
  apiBaseUrl,
  query,
  searchPath = '/',
  queryParam = 's',
  toolbarContainer = null,
  showCount = false,
  eventDetailsUrl,
  groupDetailsUrl,
  serveDetailsUrl,
  pageSize = 6,
  onResult,
}: SiteSearchProps) {
  const [data, setData] = useState<SiteSearchData | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [limits, setLimits] = useState({ events: pageSize, groups: pageSize, serve: pageSize });
  const [explainSeeJesus, setExplainSeeJesus] = useState(false);
  const [campus, setCampus] = useState<SelectedCampus | null>(() => getSelectedCampus());
  const [scope, setScope] = useState<Scope>('campus');

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setStatus('loading');
    setData(null);
    setLimits({ events: pageSize, groups: pageSize, serve: pageSize });

    fetchSearch(apiBaseUrl, query, signal)
      .then((d) => {
        setData(d);
        setStatus('ready');
      })
      .catch((err) => {
        if (signal.aborted) return;
        console.error('Site search: could not load MinistryPlatform results', err);
        setStatus('error');
      });

    fetchLocations(apiBaseUrl, signal)
      .then((l) => setLocations(l))
      .catch((err) => {
        if (!signal.aborted) console.warn('Site search: campuses unavailable', err);
      });

    return () => controller.abort();
  }, [apiBaseUrl, query, pageSize]);

  // Follow the site header's campus picker, on this page and from other tabs.
  useEffect(() => onCampusChange(setCampus), []);

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);

  const campusId = campus?.congregationId ?? null;
  const scoped = campusId !== null && scope === 'campus';
  const shown = useMemo(() => {
    if (!data) return null;
    if (!scoped) return data;
    const mine = (id: number | null) => id === campusId || id === CHURCH_WIDE;
    return {
      ...data,
      events: data.events.filter((e) => mine(e.congregationId)),
      groups: data.groups.filter((g) => mine(g.campusId)),
      opportunities: data.opportunities.filter((o) => mine(o.congregationId)),
    };
  }, [data, scoped, campusId]);

  // The host's count line follows what is on show.
  useEffect(() => {
    if (status === 'loading') return;
    if (status === 'error' || !shown) {
      onResult?.(null);
      return;
    }
    onResult?.({
      events: shown.events.length,
      groups: shown.groups.length,
      opportunities: shown.opportunities.length,
      all: data ? data.events.length + data.groups.length + data.opportunities.length : 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, shown]);

  const name = campus ? campusName(campus, locations) : null;
  const all = data ? data.events.length + data.groups.length + data.opportunities.length : 0;
  const total = shown ? shown.events.length + shown.groups.length + shown.opportunities.length : 0;

  // The search box and, once there is something to scope, the campus chips.
  // In the title band when the page has one, else above the results.
  const toolbar = (
    <div
      className={`flex flex-col gap-4 text-[#1c2b39] ${toolbarContainer ? 'mx-auto max-w-2xl items-center' : ''}`}
    >
      {showCount && status !== 'loading' && (
        <p className="result-num -mt-1 mb-2 text-base">
          {total === 1 ? '1 result found' : `${total.toLocaleString('en-US')} results found`}
        </p>
      )}
      <SearchBox query={query} searchPath={searchPath} queryParam={queryParam} />
      {campus && status === 'ready' && all > 0 && (
        <div
          className={`flex flex-wrap items-center gap-2 ${toolbarContainer ? 'justify-center' : ''}`}
          role="group"
          aria-label="Campus"
        >
          <ToggleChip active={scope === 'campus'} onClick={() => setScope('campus')}>
            <CampusIcon congregationId={campus.congregationId} className="h-4 w-4" />
            {name}
          </ToggleChip>
          <ToggleChip active={scope === 'all'} onClick={() => setScope('all')}>
            All campuses
          </ToggleChip>
          {total === 0 && (
            <span className="basis-full text-sm text-gray-600">
              Nothing at {name} or church-wide for &ldquo;{query}&rdquo;. Try all campuses.
            </span>
          )}
        </div>
      )}
    </div>
  );
  const placedToolbar = (
    <CampusSvgProvider value={svgMap}>
      {toolbarContainer ? createPortal(toolbar, toolbarContainer) : toolbar}
    </CampusSvgProvider>
  );

  if (status === 'loading') {
    return (
      <div className="flex flex-col gap-6 text-[#1c2b39]" aria-busy="true">
        {placedToolbar}
        <p className={EYEBROW}>Searching events, groups and serve opportunities</p>
        <div className={GRID}>
          <EventCardSkeleton />
          <div className="hidden sm:block">
            <EventCardSkeleton />
          </div>
          <div className="hidden lg:block">
            <EventCardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  // A failure, or nothing in MP: the search box stays, WordPress's results follow.
  if (status === 'error' || !data || !shown || all === 0) {
    return <div className="flex flex-col gap-6 text-[#1c2b39]">{placedToolbar}</div>;
  }

  const links = {
    eventDetails: eventDetailsUrl || data.links.eventDetails,
    groupDetails: groupDetailsUrl || data.links.groupDetails,
    serveDetails: serveDetailsUrl || data.links.serveDetails,
  };
  const q = encodeURIComponent(query);

  return (
    <CampusSvgProvider value={svgMap}>
      <div className="flex flex-col gap-12 text-[#1c2b39]">
        {placedToolbar}

        <Section
          title="Events"
          count={shown.events.length}
          countLabel={pluralEvents(shown.events.length)}
          seeAllHref={`${data.links.allEvents}${q}`}
          seeAllLabel="Search all events"
          shown={Math.min(limits.events, shown.events.length)}
          total={shown.events.length}
          onMore={() => setLimits((l) => ({ ...l, events: l.events + pageSize * 2 }))}
        >
          {shown.events.slice(0, limits.events).map((e) => (
            <EventCard key={e.key} event={e} dates={e.dates} detailsUrl={links.eventDetails} />
          ))}
        </Section>

        <Section
          title="Groups"
          count={shown.groups.length}
          countLabel={pluralGroups(shown.groups.length)}
          seeAllHref={`${data.links.allGroups}${q}`}
          seeAllLabel="Search all groups"
          shown={Math.min(limits.groups, shown.groups.length)}
          total={shown.groups.length}
          onMore={() => setLimits((l) => ({ ...l, groups: l.groups + pageSize * 2 }))}
        >
          {shown.groups.slice(0, limits.groups).map((g) => (
            <GroupCard
              key={g.id}
              group={g}
              detailsUrl={links.groupDetails}
              onExplainSeeJesus={() => setExplainSeeJesus(true)}
            />
          ))}
        </Section>

        <Section
          title="Serve"
          count={shown.opportunities.length}
          countLabel={pluralOpportunities(shown.opportunities.length)}
          seeAllHref={`${data.links.allServe}${q}`}
          seeAllLabel="Search all serve opportunities"
          shown={Math.min(limits.serve, shown.opportunities.length)}
          total={shown.opportunities.length}
          onMore={() => setLimits((l) => ({ ...l, serve: l.serve + pageSize * 2 }))}
        >
          {shown.opportunities.slice(0, limits.serve).map((o) => (
            <ServeCard key={o.id} opportunity={o} detailsUrl={links.serveDetails} />
          ))}
        </Section>

        <SeeJesusSheet open={explainSeeJesus} onClose={() => setExplainSeeJesus(false)} />
      </div>
    </CampusSvgProvider>
  );
}
